<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesDetailFactList from './SalesDetailFactList.vue';

const props = defineProps({
  summary: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  transaction: { type: Object, required: true },
});

const emit = defineEmits(['edit']);
const { t } = useI18n();
const profile = computed(() => props.transaction.profile || {});
const property = computed(() => props.transaction.property || {});
const address = computed(() => props.transaction.address || {});
const codeItems = computed(() => [
  {
    key: 'code',
    label: t('SALES.DELIVERY.SUMMARY.PROPERTY.CODE'),
    value: property.value.code || props.transaction.reference,
  },
]);

const addressItems = computed(() => {
  const items = [
    {
      key: 'street',
      label: t('SALES.DETAIL.PROPERTY_FIELDS.STREET'),
      value: address.value.endereco,
      copy: true,
    },
    {
      key: 'neighborhood',
      label: t('SALES.DETAIL.PROPERTY_FIELDS.NEIGHBORHOOD'),
      value: address.value.bairro,
    },
    {
      key: 'city',
      label: t('SALES.DETAIL.PROPERTY_FIELDS.CITY'),
      value: address.value.cidade,
    },
    {
      key: 'state',
      label: t('SALES.DETAIL.PROPERTY_FIELDS.STATE'),
      value: address.value.uf,
    },
    {
      key: 'zip',
      label: t('SALES.DETAIL.PROPERTY_FIELDS.ZIP'),
      value: address.value.cep,
      copy: true,
    },
    {
      key: 'number',
      label: t('SALES_PARTIES.FORM.NUMBER'),
      value: profile.value.numero || address.value.numero,
    },
    {
      key: 'unit',
      label: t('SALES_PARTIES.FORM.UNIT'),
      value: profile.value.unidade || address.value.unidade,
    },
    {
      key: 'complement',
      label: t('SALES_PARTIES.FORM.COMPLEMENT'),
      value: profile.value.complemento || address.value.complemento,
    },
  ];
  if (!props.summary) return items;
  return [
    items[4],
    items[0],
    items[5],
    items[6],
    items[7],
    items[2],
    items[3],
    items[1],
  ];
});

const detailAboutItems = computed(() => {
  const items = addressItems.value;
  return [
    codeItems.value[0],
    items[4],
    items[0],
    items[5],
    items[6],
    items[7],
    items[2],
    items[3],
    items[1],
  ];
});

const registryItems = computed(() => {
  const items = [
    {
      key: 'registration',
      label: props.summary
        ? t('SALES.DELIVERY.SUMMARY.PROPERTY.REGISTRATION')
        : t('SALES.DETAIL.PROPERTY_FIELDS.REGISTRATION'),
      value: profile.value.matricula,
    },
    {
      key: 'municipal',
      label: props.summary
        ? t('SALES.DELIVERY.SUMMARY.PROPERTY.MUNICIPAL_REGISTRATION')
        : t('SALES.DETAIL.PROPERTY_FIELDS.MUNICIPAL_REGISTRATION'),
      value: profile.value.inscricaoMunicipal,
    },
    {
      key: 'rgi',
      label: t('SALES.DETAIL.PROPERTY_FIELDS.RGI'),
      value: profile.value.rgi,
    },
    {
      key: 'writing',
      label: t('SALES.DETAIL.PROPERTY_FIELDS.WRITING'),
      value: profile.value.escritura || profile.value.tipo_escritura,
    },
    {
      key: 'parking-spaces',
      label: t('SALES.DELIVERY.SUMMARY.PROPERTY.PARKING_SPACES'),
      value: profile.value.vagas || profile.value.vaga,
    },
    {
      key: 'executed-at',
      label: t('SALES.DELIVERY.SUMMARY.PROPERTY.EXECUTED_AT'),
      value: profile.value.lavrada,
    },
    {
      key: 'book',
      label: t('SALES.DELIVERY.SUMMARY.PROPERTY.BOOK'),
      value: profile.value.livro,
    },
    {
      key: 'page',
      label: t('SALES.DELIVERY.SUMMARY.PROPERTY.PAGE'),
      value: profile.value.folha,
    },
    {
      key: 'act',
      label: t('SALES.DELIVERY.SUMMARY.PROPERTY.ACT'),
      value: profile.value.ato,
    },
  ];
  if (!props.summary) return items;
  return [
    items[3],
    items[4],
    items[0],
    items[1],
    items[2],
    items[5],
    items[6],
    items[7],
    items[8],
  ];
});
</script>

<template>
  <LaisCard
    v-if="summary"
    :title="t('SALES.DELIVERY.SUMMARY.PROPERTY.CODE')"
    prefix-icon="i-lucide-house"
  >
    <SalesDetailFactList :items="codeItems" />
  </LaisCard>

  <LaisCard
    v-else
    :title="t('SALES.DETAIL.SALE_SUMMARY.PROPERTY_TITLE')"
    prefix-icon="i-lucide-house"
  >
    <SalesDetailFactList
      class="lg:grid-cols-3"
      data-test="sales-detail-property-about-facts"
      :items="detailAboutItems"
    />
    <div v-if="canEdit" class="mt-4 flex justify-end">
      <LaisPrimaryButton
        data-test="sales-detail-edit-address"
        icon="i-lucide-pencil"
        variant="link"
        size="sm"
        @click="emit('edit', 'address')"
      >
        {{ t('SALES_PROPERTY.ACTIONS.EDIT') }}
      </LaisPrimaryButton>
    </div>
  </LaisCard>

  <LaisCard
    v-if="summary"
    :title="t('SALES.DELIVERY.SUMMARY.PROPERTY.ADDRESS_TITLE')"
    prefix-icon="i-lucide-map-pin"
  >
    <SalesDetailFactList :items="addressItems" />
  </LaisCard>

  <LaisCard
    :title="
      summary
        ? t('SALES.DELIVERY.SUMMARY.PROPERTY.REGISTRY_TITLE')
        : t('SALES.DETAIL.PROPERTY_SECTIONS.REGISTRY')
    "
    prefix-icon="i-lucide-landmark"
  >
    <SalesDetailFactList :items="registryItems" />
    <div v-if="canEdit && !summary" class="mt-4 flex justify-end">
      <LaisPrimaryButton
        data-test="sales-detail-edit-registry"
        icon="i-lucide-pencil"
        variant="link"
        size="sm"
        @click="emit('edit', 'registry')"
      >
        {{ t('SALES_PROPERTY.ACTIONS.EDIT') }}
      </LaisPrimaryButton>
    </div>
  </LaisCard>
</template>
